import { Space, Tag as AntdTag, Tooltip } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import { ICategory } from "../../../models/category/response";
import { ICategoryDiscount } from "../../../models/discount-code/response";
import EndPoints from "../../../services/end-points";

interface IProps {
    categoryDiscounts?: ICategoryDiscount[];
}

const getCategoryLevelKey = (category?: ICategory) => {
    if (!category) {
        return "unknown_category_level";
    }

    if (category?.parent) {
        return "category_level_three";
    }

    if (category?.grand) {
        return "category_level_two";
    }

    return "category_level_one";
};

const getCategoryPath = (category?: ICategory) => {
    if (!category) {
        return "";
    }

    return [category?.grand?.name, category?.parent?.name, category?.name]
        .filter(Boolean)
        .join(" / ");
};

const CategoryDiscountsDisplay: React.FC<IProps> = ({ categoryDiscounts = [] }) => {
    const { t } = useTranslation();
    const { direction } = useContext(AppContext);
    const [categories, setCategories] = useState<ICategory[]>([]);

    const categoryById = useMemo(() => {
        return new Map(categories.map((category) => [Number(category?.id), category]));
    }, [categories]);

    useEffect(() => {
        if (!categoryDiscounts?.length) {
            setCategories([]);
            return;
        }

        let isActive = true;
        const getCategories = async () => {
            try {
                const { data } = await EndPoints.category.getAllCategories({ perPage: -1 });

                if (isActive) {
                    setCategories(data?.data ?? []);
                }
            } catch (error) {
                if (isActive) {
                    setCategories([]);
                }
            }
        };

        getCategories();

        return () => {
            isActive = false;
        };
    }, [categoryDiscounts?.length, direction]);

    if (!categoryDiscounts?.length) {
        return <>-</>;
    }

    return (
        <Space wrap size={[6, 6]}>
            {categoryDiscounts.map((categoryDiscount) => {
                const category =
                    categoryById.get(Number(categoryDiscount?.categoryId)) ??
                    categoryDiscount?.category;
                const categoryName = category?.name ?? `#${categoryDiscount?.categoryId}`;
                const levelLabel = t(getCategoryLevelKey(category));
                const path = getCategoryPath(category);
                const title = path
                    ? `${path} - ${levelLabel} - ${categoryDiscount?.discountValueProduct}%`
                    : `${categoryName} - ${levelLabel} - ${categoryDiscount?.discountValueProduct}%`;

                return (
                    <Tooltip title={title} key={categoryDiscount?.categoryId}>
                        <AntdTag color="blue">
                            {categoryName} | {levelLabel} | {categoryDiscount?.discountValueProduct}%
                        </AntdTag>
                    </Tooltip>
                );
            })}
        </Space>
    );
};

export default CategoryDiscountsDisplay;
